import {
  Button,
  Box,
  Popover,
  Typography,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { Row } from '@tanstack/react-table';
import { BenefitSet, EnabledBenefitsEnum } from '@pxtra-client-lib-legacy/gen';
import { useTranslation } from 'react-i18next';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useState } from 'react';
import { BenefitsSetRowItem } from '../columns';
import { PeopleIcon } from '../../../../icons';

/**
 * Cell component that displays a button showing the number of how often the benefit is included in benefit sets
 * When clicked, it will trigger an overlay with detailed benefits set information
 */
interface BenefitSetUpdatesCellProps {
  row: Row<BenefitsSetRowItem>;
  benefitSets: BenefitSet[];
}

export const BenefitSetCell: React.FC<BenefitSetUpdatesCellProps> = ({ row, benefitSets }) => {
  const { t } = useTranslation();

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  const handleBenefitsClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  if (row.original.custom) {
    return <Typography className="pl-4">-</Typography>;
  }

  return (
    <>
      <Button
        variant="text"
        endIcon={<KeyboardArrowDownIcon />}
        className="flex justify-between px-3 py-2 font-normal rounded-xl hover:bg-grey-200 min-w-40"
        sx={{
          textTransform: 'none',
          borderRadius: '12px'
        }}
        onClick={handleBenefitsClick}
      >
        {row.original.benefitInBenefitSetCount}{' '}
        {t('benefitSet:benefitSet', {
          count: row.original.benefitInBenefitSetCount
        })}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
        BackdropProps={{
          invisible: true,
          style: { backgroundColor: 'transparent', opacity: '0' }
        }}
        PaperProps={{
          className: 'flex flex-col w-[350px] max-h-[420px] rounded-xl overflow-hidden shadow-500'
        }}
      >
        <Box className="p-6 pb-3">
          <Typography className="font-bold ">{t(`benefits:${row.original.id}`)} </Typography>
          <Typography className="text-grey-600 text-sm">
            {t('benefitSet:availableInBenefitSet', {
              count: row.original.benefitInBenefitSetCount
            })}{' '}
          </Typography>
        </Box>

        <Divider />
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              px: 2,
              py: 1.5,
              borderColor: 'divider'
            }}
          >
            <Typography variant="body2" className="text-xs font-medium text-grey-600 pl-2">
              {t('benefitSet:benefitSet_one')}
            </Typography>

            <Typography
              variant="body2"
              className="text-xs text-right pr-3 font-medium text-grey-600"
              sx={{
                flex: '1 1 25%'
              }}
            >
              {t('common:EMPLOYEES')}
            </Typography>
          </Box>
          <List
            className="flex-1 overflow-auto py-0 mx-2"
            sx={{
              '&::-webkit-scrollbar': {
                display: 'none'
              },
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '& .MuiListItem-root:last-child': {
                mb: 0
              }
            }}
          >
            {benefitSets
              ?.filter((benefitSet) =>
                benefitSet.enabled_benefits?.includes(row.original.id as EnabledBenefitsEnum)
              )
              .map(({ name, employee_count }, index) => (
                <ListItem
                  key={name}
                  className="flex px-2 rounded-lg justify-between"
                  sx={{
                    backgroundColor: index % 2 === 1 ? 'grey.50' : 'transparent'
                  }}
                >
                  <ListItemText
                    className="pl-2 break-words flex-grow"
                    primary={t(`benefits:${name}`)}
                  />
                  <Box className="flex items-center gap-2 min-w-[60px] pr-8">
                    <PeopleIcon className="[&_path]:fill-none" />
                    <Typography className="min-w-[30px]">{employee_count}</Typography>
                  </Box>
                </ListItem>
              ))}
          </List>
        </Box>
      </Popover>
    </>
  );
};
